import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Link, Navigate, NavLink, Outlet } from 'react-router-dom';

import LogoIcon from '../../../assets/images/Learnit_logo.svg';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import {
  getOpsNotifications,
  readOpsNotification,
  TOpsNotification,
} from '../../../requests/opsRecords';
import { fetchUser } from '../../../store/reducers/actionCreators';
import SidebarAccount from '../SidebarAccount';

/** Where a mention notification links to, by the kind of record. */
function recordPath(n: TOpsNotification): string {
  switch (n.recordObject) {
    case 'class':
      return PATH.opsClassEdit.replace(':id', n.recordId);
    case 'account':
      return PATH.opsAccountView.replace(':id', n.recordId);
    case 'contact':
      return PATH.opsContactEdit.replace(':id', n.recordId);
    case 'case':
      return `${PATH.opsCases}?id=${n.recordId}`;
    case 'program':
      return `${PATH.opsPrograms}?id=${n.recordId}`;
    default:
      return PATH.adminOps;
  }
}

/**
 * The mention inbox: a bell with the unread count, a list on click. Marks
 * a notification read when its link is followed.
 */
const MentionBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['opsNotifications'],
    queryFn: getOpsNotifications,
    refetchInterval: 60_000,
  });
  const markRead = useMutation({
    mutationFn: (id: string) => readOpsNotification(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['opsNotifications'] }),
  });
  const unread = data?.unread ?? 0;
  const items = (data?.items ?? []).slice(0, 20);
  return (
    <div className="relative px-3 pt-2">
      <button
        type="button"
        className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm text-gray-three transition-colors hover:bg-grey-50 hover:text-gray-four"
        onClick={() => setOpen((o) => !o)}
        aria-label="Mentions"
      >
        <span>Mentions</span>
        {unread > 0 ? (
          <span className="rounded-full bg-blue-primary px-2 py-0.5 text-[11px] font-semibold text-white">
            {unread}
          </span>
        ) : (
          <span className="text-xs text-gray-two">none unread</span>
        )}
      </button>
      {open && (
        <div className="absolute bottom-full left-3 z-40 mb-1 w-80 overflow-hidden rounded-xl border border-white-regular bg-white shadow-light">
          <div className="border-b border-white-three px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-bold">
            Mentions
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-two">
                Nobody has mentioned you yet.
              </div>
            ) : (
              items.map((n) => (
                <Link
                  key={n.id}
                  to={recordPath(n)}
                  onClick={() => {
                    if (!n.readAt) markRead.mutate(n.id);
                    setOpen(false);
                  }}
                  className={`block border-b border-white-three px-4 py-2.5 text-sm transition-colors hover:bg-blue-100/40 ${
                    n.readAt ? 'text-gray-three' : 'text-gray-four'
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-2 text-xs">
                    <span className={n.readAt ? '' : 'font-semibold'}>
                      {n.fromName ?? n.fromEmail ?? 'Someone'}
                    </span>
                    <span className="text-gray-two">
                      {n.recordObject} · {new Date(n.at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="mt-0.5 line-clamp-2">{n.snippet}</div>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const GROUPS = [
  {
    label: 'Delivery',
    items: [
      { to: PATH.opsCalendar, label: 'Calendar' },
      { to: PATH.opsRunBoard, label: 'Run board' },
      { to: PATH.opsClasses, label: 'Classes' },
      { to: PATH.opsAssignments, label: 'Assignments' },
      { to: PATH.opsRegistrations, label: 'Registrations' },
      { to: PATH.opsCatalogue, label: 'Catalogue' },
      { to: PATH.opsTrainer, label: 'Trainer view' },
      { to: PATH.opsInstructors, label: 'Instructors' },
    ],
  },
  {
    label: 'Customers',
    items: [
      { to: PATH.opsAccounts, label: 'Accounts' },
      { to: PATH.opsContacts, label: 'Contacts' },
      { to: PATH.opsPrograms, label: 'Programs' },
    ],
  },
  {
    label: 'Care',
    items: [
      { to: PATH.opsCases, label: 'CX inbox' },
      { to: PATH.opsComms, label: 'Comms' },
      { to: PATH.opsEvals, label: 'Evals' },
    ],
  },
  {
    label: 'Data',
    items: [
      { to: PATH.opsReports, label: 'Reports' },
      { to: PATH.opsSync, label: 'Salesforce sync' },
      { to: PATH.opsHubspot, label: 'HubSpot' },
      { to: PATH.opsCalendarSync, label: 'Outlook sync' },
    ],
  },
];

/**
 * The staff (ops) shell — deliberately separate from the learner
 * NavbarLayout: its own sidebar, no dependency on the learner chrome or on
 * the user having a company. Admin-only; the API enforces the same rule.
 */
export const OpsLayout: React.FC = () => {
  const { user, isLoading } = useAppSelector((state) => state.userReducer);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchUser());
  }, []);

  if (isLoading) {
    return null;
  }
  if (!user) {
    return <Navigate to={PATH.login} replace />;
  }
  if (user.role !== 'admin') {
    return <Navigate to={PATH.upcomingClasses} replace />;
  }

  return (
    <div className="min-h-screen bg-grey-50 text-gray-four">
      <aside className="fixed inset-y-0 left-0 z-30 flex w-60 flex-col border-r border-white-regular bg-white">
        <NavLink to="/ops" end className="block px-6 pb-4 pt-6">
          <img src={LogoIcon} alt="Learn It" className="h-6 w-auto" />
          <span className="mt-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-bold">
            Operations
          </span>
        </NavLink>
        <nav className="flex-1 overflow-y-auto px-3 pb-6">
          {GROUPS.map((g) => (
            <div key={g.label} className="mt-4 first:mt-1">
              <div className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-two">
                {g.label}
              </div>
              {g.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className={({ isActive }) =>
                    `mb-0.5 block rounded-lg px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? 'bg-blue-100 font-semibold text-blue-primary'
                        : 'text-gray-three hover:bg-grey-50 hover:text-gray-four'
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <MentionBell />
        <SidebarAccount user={user} subtitle="Staff · Admin" />
      </aside>
      <main className="ml-60 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
};

export default OpsLayout;
